/**
 * Wizard Draft Store
 * 
 * Persists draft data for wizards (templates, objects, documents, dialogs) 
 * so that form data is not lost when switching tabs.
 * 
 * Uses the tab path as the key for new items, and the item ID for existing items.
 * Data is stored in IndexedDB and automatically cleaned up when tabs are closed
 * or items are saved.
 */

import { storeRegistry } from "@/rerun-manager/GenericStore";

// Draft data types for different wizard types
export interface TemplateDraftData {
  name: string;
  description: string;
  tags: string[];
  types: string[];
  color: string;
  sections: any[];
  renderOrder: string[];
  owner?: string | null;
  editPermissions?: 'Open' | 'Locked';
  status?: string;
  // Wizard mode state - persisted across tab switches
  wizardMode?: 'create' | 'edit' | 'view';
}

export interface ObjectDraftData {
  name: string;
  tags: string[];
  identifiers: string[];
  sections: any[];
  blueprintId?: string;
  owner?: string | null;
  editPermissions?: 'Open' | 'Locked';
  status?: string;
}

export interface DocumentDraftData {
  name: string;
  blocks: any[];
  widgetData: Record<string, unknown>;
  tags: string[];
  settings: Record<string, unknown>;
}

export interface DialogDraftData {
  dialogName: string;
  dialogKey: string;
  description: string;
  translatable: boolean;
  speakerMode: string;
  speakerTypeId: string | null;
  speakerTemplateIds: string[];
  dialogVariables: any[];
  sections: any[];
  // Flow mode state
  viewMode?: string;
  localFlowSections?: any[];
  flowNodes?: any[];
  flowEdges?: any[];
  flowInitialized?: boolean;
  flowViewport?: { x: number; y: number; zoom: number }; // Persist zoom and pan position
}

export type WizardDraftType = 'template' | 'object' | 'document' | 'dialog';

export interface WizardDraft {
  id: string; // Tab path for new items, or "item-{itemId}" for existing
  draftType: WizardDraftType;
  gameId: string;
  itemId?: string; // Only set for existing items
  data: TemplateDraftData | ObjectDraftData | DocumentDraftData | DialogDraftData;
  createdAt: string;
  updatedAt: string;
}

// Create the wizard draft store using GenericStore for IndexedDB persistence
export const wizardDraftStore = storeRegistry.getStore<WizardDraft>({
  key: 'wizard-drafts',
  ttlMs: 1000 * 60 * 60 * 24 * 7, // 7 days TTL
});

/**
 * Generate a draft key for a wizard
 * For new items: uses the tab path (e.g., "/templates/new")
 * For existing items: uses "item-{itemId}"
 */
export function getDraftKey(tabPath: string, itemId?: string): string {
  if (itemId && itemId !== 'new') {
    return `item-${itemId}`;
  }
  return tabPath;
}

/**
 * Get draft data for a wizard
 */
export function getDraft(draftKey: string): WizardDraft | undefined {
  const allDrafts = wizardDraftStore.getData();
  return allDrafts.find(draft => draft.id === draftKey);
}

/**
 * Check if a draft exists
 */
export function hasDraft(draftKey: string): boolean {
  return getDraft(draftKey) !== undefined;
}

/**
 * Save draft data for a wizard
 */
export function saveDraft(
  draftKey: string,
  draftType: WizardDraftType,
  gameId: string,
  data: WizardDraft['data'],
  itemId?: string
): void {
  const allDrafts = wizardDraftStore.getData();
  const existingIndex = allDrafts.findIndex(d => d.id === draftKey);
  const now = new Date().toISOString();

  if (existingIndex >= 0) {
    // Update existing draft
    const updatedDraft: WizardDraft = {
      ...allDrafts[existingIndex],
      data,
      updatedAt: now,
    };
    const newDrafts = [...allDrafts];
    newDrafts[existingIndex] = updatedDraft;
    wizardDraftStore.setData(newDrafts);
  } else {
    // Create new draft
    const newDraft: WizardDraft = {
      id: draftKey,
      draftType,
      gameId,
      itemId: itemId && itemId !== 'new' ? itemId : undefined,
      data,
      createdAt: now,
      updatedAt: now,
    };
    wizardDraftStore.setData([...allDrafts, newDraft]);
  }
}

/**
 * Remove draft data (after save or discard)
 */
export function removeDraft(draftKey: string): void {
  const allDrafts = wizardDraftStore.getData();
  const newDrafts = allDrafts.filter(d => d.id !== draftKey);
  wizardDraftStore.setData(newDrafts);
}

/**
 * Remove all drafts for a specific game
 */
export function removeAllDraftsForGame(gameId: string): void {
  const allDrafts = wizardDraftStore.getData();
  const newDrafts = allDrafts.filter(d => d.gameId !== gameId);
  wizardDraftStore.setData(newDrafts);
}

/**
 * Remove all drafts of a specific type
 */
export function removeAllDraftsOfType(draftType: WizardDraftType, gameId?: string): void {
  const allDrafts = wizardDraftStore.getData();
  const newDrafts = allDrafts.filter(d => 
    !(d.draftType === draftType && (!gameId || d.gameId === gameId))
  );
  wizardDraftStore.setData(newDrafts);
}

/**
 * Get all drafts (for debugging or admin purposes)
 */
export function getAllDrafts(): WizardDraft[] {
  return wizardDraftStore.getData();
}

/**
 * Clear all drafts
 */
export async function clearAllDrafts(): Promise<void> {
  await wizardDraftStore.clearCache();
}

/**
 * Subscribe to draft changes
 */
export function subscribeToDrafts(callback: (drafts: WizardDraft[]) => void): () => void {
  return wizardDraftStore.subscribe(callback);
}

// Type guards for draft data
export function isTemplateDraft(draft: WizardDraft): draft is WizardDraft & { data: TemplateDraftData } {
  return draft.draftType === 'template';
}

export function isObjectDraft(draft: WizardDraft): draft is WizardDraft & { data: ObjectDraftData } {
  return draft.draftType === 'object';
}

export function isDocumentDraft(draft: WizardDraft): draft is WizardDraft & { data: DocumentDraftData } {
  return draft.draftType === 'document';
}

export function isDialogDraft(draft: WizardDraft): draft is WizardDraft & { data: DialogDraftData } {
  return draft.draftType === 'dialog';
}
